/**
 * 62.48: the action clock's bot knobs — the brains' weights (B2's jobs, B3's roles, B1's fight spots and the retreat's)
 * and the rules every action brain shares (cover, healing, escorts, the team picture, hearing, aim, the deliberation
 * delay, prone, the gun brain's search tier and danger close, navigation), as `action.<group>.<key>` rows flattened
 * from ACTION_PARAMS. `createActionBot` reads them from its options' `params` (default ACTION_PARAMS — today's bot).
 * Evolved weights (B4's `--weights` files) are values over these rows, not rows of their own.
 */
import type { BotParamDef, BotParamSet } from '../types';
import { ACTION_DELIBERATION_DELAY, GUN_SEARCH_TIER } from '../constants';
import { DEFAULT_JOB_WEIGHTS, ROLE_WEIGHTS, type ActionRole, type JobWeights } from './actionJobs';
import { DEFAULT_POSITION_WEIGHTS, RETREAT_WEIGHTS, type PositionWeights } from './actionPositions';
import { catalogueOf, entriesUnder, withPath, type RangeRule } from './botParamsLib';
import { STRIKE_CREW_PEOPLE, STRIKE_MIN_CONFIDENCE } from './strikeRule';

export interface ActionParams {
  /** B2 (utility brain): the jobs' weights. */
  jobs: JobWeights;
  /** B3 (roles brain): each role's job weights. */
  roles: Record<ActionRole, JobWeights>;
  /** B1: the fight spot's scorer. */
  positions: PositionWeights;
  /** B2: the retreat spot's scorer. */
  retreat: PositionWeights;
  rules: {
    /** 64.9 (decision 115): ticks from a decision to its plan applying. */
    delay: number;
    /** At or below this health, look for cover. */
    coverHealth: number;
    /** Seconds behind cover before coming back out. */
    coverHold: number;
    /** Seconds before seeking cover again. */
    coverCooldown: number;
    /** 65.7: at or below this health, out of the fight, a bandage goes on. */
    healBelow: number;
    /** 65.10: people a medic walks to a hurt teammate who has no bandage. */
    medicWalk: number;
    /** Seconds between aim-error re-rolls. */
    aimReroll: number;
    /** Ticks between job and fight-spot choices (§14 B1). */
    positionPeriod: number;
    /** B3 assault: lead the charge by this many person-lengths. */
    escortLead: number;
    /** B3 defence on the move: trail the charge by this many. */
    escortTrail: number;
    /** §13: below this a shared track is not worth walking to. */
    teamTrackMinConfidence: number;
    /** L2: a heard shot's position error, × its distance. */
    heardError: number;
    /** 415 step 3: the utility brain's allies "near" (people). */
    alliesNearPeople: number;
    /** 415 step 3: a held site counts as threatened with a seen enemy inside this many capture radii (the utility brain). */
    siteThreatCaptures: number;
    /** 415 step 3: a hold's member looks for its threat among the known enemies within this many sight ranges. */
    holdThreatSights: number;
    /** 415 step 3: the old cover mode (coverMoves off) leaves cover past coverHold plus this many seconds, whatever. */
    coverHoldExtra: number;
    /** 415 step 3: out of sight, a reload when the magazine is under this share of a full one. */
    reloadBelowShare: number;
    /** 415 step 3: a reload longer than this many weapon draws is slow (the sidearm comes out instead, unsheltered). */
    slowReloadDraws: number;
  };
  /** The prone rule. */
  prone: {
    /** Seconds a bot counts itself suppressed after being hurt. */
    suppressedSeconds: number;
    /** A long shot is past this many times its preferred range. */
    longRange: number;
  };
  /** 181's attention (GameConfig.attention): what a bot sees by where it faces, and the startle of a hit from outside it. */
  attention: {
    /** Half-angle of the focus arc ahead (degrees): an enemy on a clear line in it is seen. */
    focusDeg: number;
    /** Half-angle of the peripheral band (degrees): an enemy in it is seen only while it fires, or as the target it keeps. */
    peripheralDeg: number;
    /** Within this many person heights an enemy is sensed in any direction. */
    nearPeople: number;
    /** A peripheral enemy that fired within this many seconds is seen. */
    firedSeconds: number;
    /** Hit within this many seconds before acquiring an enemy it had not seen: a startle. */
    hitSeconds: number;
    /** The startle added to its reaction (s), × (1 + how absorbed it was in its own fight). */
    startleSeconds: number;
    /** Engaged this long with its own target: fully absorbed (s). */
    absorbSeconds: number;
    /** This many enemies coming into view at once: no startle (a big obvious threat pulls the focus at once). */
    bigThreat: number;
    /** Hurt, or a contact heard or seen, within this many seconds: still in the fight (no bandage in the open) though unseen. */
    fightSeconds: number;
  };
  /** The gun brain (64.11). */
  gun: {
    /** Seats under threat: run for an empty gun within this many person lengths. */
    runLengths: number;
    /** How long a team-picture track stays a ghost the gun may shell (s). */
    trackSeconds: number;
    /** Hold a shot that would land within its own blast's reach of the gun (core/seats gunDangerClose). */
    honourDangerClose: boolean;
    /** The fire-search work budget, whatever the crew's skill (GUN_SEARCH_TIER). */
    searchTier: typeof GUN_SEARCH_TIER;
  };
  /** Phase 59.4: skill → how the bot plays (actionBot actionSkill): each a base and a slope over skill (or its lack). */
  skill: {
    reactionBase: number; reactionPerSkill: number;
    turnRateBase: number; turnRatePerSkill: number;
    /** The aim's error (rad): base + perWeak × (1 − skill). 69.43 (DECISIONS 275, the owner: "double + a"): halved at every difficulty (0.035 / 0.04 before) — each a step up, the ladder kept. */
    aimNoiseBase: number; aimNoisePerWeak: number;
    settleFloorBase: number; settleFloorPerSkill: number;
    strafeBase: number; strafePerSkill: number;
    /** 62.52: how much of the wind's drift the aim allows for (0 none … 1 all): a base and a slope over skill — high skill leads into the wind, low skill does not. */
    windBase: number; windPerSkill: number;
    /** 415 step 3: a reaction is the skill's × (reactionJitterMin + reactionJitterSpread × a draw in [0, 1)). */
    reactionJitterMin: number; reactionJitterSpread: number;
  };
  /** 65.13 / 65.14: how each gun fights — its fight band × `band`, the SMG's hold-fire distance (people), the LMG's bursts (s on, s off). */
  guns: {
    smg: { band: number; holdBeyondPeople: number };
    lmg: { band: number; burstOn: number; burstOff: number };
  };
  /** Lane B's kit rules (the brain-rules row). */
  kit: {
    /** A drone goes up after this long with nothing seen (s). */
    droneQuietSeconds: number;
    /** One mark an enemy this often (s). */
    markEverySeconds: number;
  };
  /** The walking row (lane C). */
  walk: {
    /** A walk looks this many person widths ahead… */
    steerLookLengths: number;
    /** …and passes a thing by this many person radii. */
    steerMargin: number;
    /** Within this long of a stuck hop, at the same spot, it does not hop again (s). */
    hopAgainSeconds: number;
    /** 69.90: over this long walking somewhere, a net way under half the stuck share of its steps is stuck too — a walk that
     * reverses every call moves every call and gets nowhere (s). */
    stuckWindowSeconds: number;
  };
  /** X31 bikes. */
  bike: {
    /** Ride for a destination further than this (people)… */
    rideFrom: number;
    /** …taking a free bike within this (people)… */
    walk: number;
    /** …and getting off within this of it (people). */
    park: number;
    /** Stuck on a bike, walk this long before taking one again (s). */
    shunSeconds: number;
  };
  /** 65.15: the grenade rule. */
  grenade: {
    /** HE: a target within this share of the throw's range… */
    heReachShare: number;
    /** …and no nearer than this many burst radii. */
    heMinBursts: number;
    /** 69.85 (a) (DECISIONS 318): HE at an enemy it cannot shoot only on a belief at most this old (s); a stale track,
     *  report or mark drew most of a side's HE, a fifth of it at enemies already dead. */
    heHiddenFreshSeconds: number;
    /** Smoke: the cloud this share of the way to the enemy… */
    smokeShare: number;
    /** …between these person heights out. */
    smokeMinPeople: number;
    smokeMaxPeople: number;
    /** 65.23 (with the side's finite stock): smoke to cover a bound only with more than this many in hand (the last ones break contact). */
    keepSmoke: number;
  };
  /** 65.29 (2): with the side's finite rocket stock, a rocket goes on a gunner in a seat or a vehicle's crew — or on a huddle of at least `stockHuddle` enemies inside its blast (the target among them); never on a man alone. */
  /** 65.29 (2b): an enemy 6×6 or technical whose hull is in sight is a rocket target — the tube drawn at `hullEdge` × the rocket's reach, fired inside it. */
  /** Bots against ships (with shipsDestructible): a ship no higher than `shipHeightPeople` over the ground is a rocket's (landing, hovering, unloading, parked, low on approach — not cruising), no higher than `shipLobHeightPeople` a 40 mm's. */
  rocket: { stockHuddle: number; hullEdge: number; shipHeightPeople: number; shipLobHeightPeople: number };
  /** 65.31 step 4: a 40 mm carrier draws it for a seen target this far out (people) — in cover, or with `huddle` enemies inside the burst (it among them); its pitch error × `pitchNoise` (a lob turns a degree into ~7 people of range at 100: the grenadier judges the arc by its ladder sight). */
  gl40: { minPeople: number; maxPeople: number; huddle: number; pitchNoise: number };
  /**
   * 185 step 5, with hit locations and armour: a bot from `headSkill` up aims at the head within `headPeople` × its skill of
   * the target (the low-skill aim centre mass); in target choice a target's distance counts × (1 + `armourWeight` × the share
   * its armour stops of the bot's round), and with a rocket or a .50 a heavy vest counts × `heavyPull` (nearer: worth it).
   */
  armourAim: { headSkill: number; headPeople: number; armourWeight: number; heavyPull: number };
  /** 65.16 / 65.15: the strike rule (strikeRule.ts) — a gun crew within `crewPeople` of its gun; nothing under `minConfidence`. */
  strike: { crewPeople: number; minConfidence: number };
  /**
   * 62.57 (2), the look slots (62.51): a team's units look and deliberate on `slots` slots of the sight period, so a look
   * tick holds a share of each side. Above `slotsAbove` units a team, enough slots for `unitsPerSlot` units each — rounded
   * up to a count that divides the sight period — so 50 a side looks on 12, not in bursts of 17 on 6. DECISIONS 384: `slots`
   * 24, the whole sight period — a slot every tick at any size (a bot still looks once a period; only its phase moves).
   */
  look: { slots: number; slotsAbove: number; unitsPerSlot: number };
  /**
   * 66.7 bound-with-cover (with coverMoves; the owner: bounding with covering fire in alternation, its dials here): the
   * movers' cover posts within `coverReachPeople` of the bound's point, which stands `offsetPeople` to the side of the
   * covering half's line to the threat (the side the movers are on: the halves leapfrog on parallel tracks, so the covering
   * fire has a lane); the covering half's within `settlePeople` of where
   * it stands; covering fire at the enemy the team knows best within `fireReachPeople` of the coverer; area fire's rhythm —
   * the bound's covering half and the play's support (68.22) — `fireBurst` seconds of every `firePeriod` (or its lean's
   * burst). Person heights and seconds.
   */
  bound: { coverReachPeople: number; offsetPeople: number; settlePeople: number; fireReachPeople: number; fireBurst: number; firePeriod: number };
  /**
   * DECISIONS 408's bots step (ai/supplyRead.ts; with GameConfig.finiteSupply, nothing without): a bot conserves while its
   * side's stock covers under `sideLow` of the issues its tickets still draw, or its own long gun is under `unitLow` of a
   * load — no area fire, aimed fire held past `holdBeyondPeople`; under a push, a member under `topUpShare` of a load (or short
   * of bandages) re-arms at a stocked crate in its walk first. Shares, person heights. Proposals.
   */
  supply: { sideLow: number; unitLow: number; holdBeyondPeople: number; topUpShare: number };
  /**
   * 66.11 under fire, take cover (with coverMoves; sweeps/2026-10-12-cover-use.md): a bot hit within `hitSeconds`, suppressed,
   * or seeing an enemy fire its way (its muzzle within `flashCone` radians of the line to the bot) — or hurt, or out of
   * rounds, as the old rule — makes for the nearest cover within `reachPeople` that hides it crouched from that shooter (at
   * most `tries` spots judged) and fights from it as a holder does from its post; it stays `holdSeconds` past the last of
   * it (and while it reloads or bandages), at most `maxSeconds`, then waits `cooldownSeconds` before the next. Person
   * heights, seconds.
   */
  underFire: { hitSeconds: number; flashCone: number; reachPeople: number; tries: number; holdSeconds: number; maxSeconds: number; cooldownSeconds: number; /** 66.19: no cover found — the search waits this long (s) before it is tried again. */ retrySeconds: number; /** 415 step 3: a fire-cover spot is judged again after this many seconds. */ judgeSeconds: number };
  /**
   * 63.8 the gaits the bots choose (the paces are GAIT's, the player's too): `sprint` between cover — a bound's leg, a
   * tactic's, making for cover hurt or pulling back; `move` for a walk farther than `movePeople` with nothing in sight;
   * otherwise the shooting walk (in sight, fighting, at a post — a hold never runs); the last `nearPeople` of any way at
   * the walk (a faster pace oversteps a post, a seat or a cover spot and circles it).
   */
  gait: { sprint: boolean; move: boolean; movePeople: number; nearPeople: number };
  /** D3 navigation (when `navigate` is on). */
  nav: {
    /** Destinations nearer than this many body radii are walked straight at. */
    minRadii: number;
    /** Replan a route at least this often (s)… */
    replan: number;
    /** …and, for the same destination drifting, no more often than this (s). */
    replanMin: number;
    /** A destination has drifted when it moved more than this share of the distance still to walk. */
    goalShift: number;
    /** Routes a bot remembers by destination. */
    routeCache: number;
    /** How long a sighting stays a threat a route steers around (s). */
    threatMemory: number;
    /** Extra route cost per unit distance for being seen (the default; `navThreatWeight` wins). */
    threatWeight: number;
  };
}

/** Today's action bot. */
export const ACTION_PARAMS: ActionParams = {
  jobs: { ...DEFAULT_JOB_WEIGHTS },
  roles: { objective: { ...ROLE_WEIGHTS.objective }, assault: { ...ROLE_WEIGHTS.assault }, defence: { ...ROLE_WEIGHTS.defence } },
  positions: { ...DEFAULT_POSITION_WEIGHTS },
  retreat: { ...RETREAT_WEIGHTS },
  rules: {
    delay: ACTION_DELIBERATION_DELAY, coverHealth: 50, coverHold: 1.6, coverCooldown: 4, healBelow: 65, medicWalk: 25,
    aimReroll: 0.4, positionPeriod: 60, escortLead: 6, escortTrail: 3, teamTrackMinConfidence: 0.25, heardError: 0.25,
    alliesNearPeople: 10, siteThreatCaptures: 3, holdThreatSights: 1.5, coverHoldExtra: 3, reloadBelowShare: 0.5, slowReloadDraws: 2,
  },
  prone: { suppressedSeconds: 2, longRange: 1.5 },
  attention: { focusDeg: 60, peripheralDeg: 110, nearPeople: 3, firedSeconds: 1, hitSeconds: 1.5, startleSeconds: 0.5, absorbSeconds: 4, bigThreat: 3, fightSeconds: 3 },
  gun: { runLengths: 8, trackSeconds: 10, honourDangerClose: true, searchTier: { ...GUN_SEARCH_TIER } },
  skill: { reactionBase: 0.6, reactionPerSkill: 0.45, turnRateBase: 4, turnRatePerSkill: 8, aimNoiseBase: 0.0175, aimNoisePerWeak: 0.02, settleFloorBase: 0.85, settleFloorPerSkill: 0.55, strafeBase: 0.3, strafePerSkill: 0.6, windBase: 0, windPerSkill: 1, reactionJitterMin: 0.75, reactionJitterSpread: 0.5 },
  guns: { smg: { band: 0.55, holdBeyondPeople: 45 }, lmg: { band: 1.15, burstOn: 0.9, burstOff: 0.35 } },
  kit: { droneQuietSeconds: 15, markEverySeconds: 10 },
  walk: { steerLookLengths: 4, steerMargin: 0.5, hopAgainSeconds: 3, stuckWindowSeconds: 2 },
  bike: { rideFrom: 30, walk: 10, park: 6, shunSeconds: 20 },
  grenade: { heReachShare: 0.9, heMinBursts: 2, heHiddenFreshSeconds: 2, smokeShare: 0.4, smokeMinPeople: 4, smokeMaxPeople: 10, keepSmoke: 1 },
  rocket: { stockHuddle: 3, hullEdge: 1.25, shipHeightPeople: 25, shipLobHeightPeople: 3 },
  gl40: { minPeople: 30, maxPeople: 150, huddle: 2, pitchNoise: 0.25 },
  armourAim: { headSkill: 0.55, headPeople: 12, armourWeight: 1.5, heavyPull: 0.6 },
  strike: { crewPeople: STRIKE_CREW_PEOPLE, minConfidence: STRIKE_MIN_CONFIDENCE },
  look: { slots: 24, slotsAbove: 25, unitsPerSlot: 25 / 6 },   // (DECISIONS 384: every tick a slot — 6 put a sixth of each side's looks on every 4th tick, 4–5× the tick between)
  gait: { sprint: true, move: true, movePeople: 6, nearPeople: 2 },
  bound: { coverReachPeople: 4, offsetPeople: 3, settlePeople: 3, fireReachPeople: 30, fireBurst: 1.2, firePeriod: 3 },
  supply: { sideLow: 0.5, unitLow: 0.25, holdBeyondPeople: 40, topUpShare: 0.6 },
  underFire: { hitSeconds: 2, flashCone: 0.35, reachPeople: 7, tries: 6, holdSeconds: 3, maxSeconds: 8, cooldownSeconds: 2, retrySeconds: 1, judgeSeconds: 1 },
  nav: { minRadii: 8, replan: 2, replanMin: 0.75, goalShift: 0.2, routeCache: 4, threatMemory: 6, threatWeight: 2 },
};

const READERS: Record<keyof ActionParams, string> = {
  jobs: 'actionJobs chooseJob (utility brain)',
  roles: 'actionJobs chooseJob (roles brain)',
  positions: 'actionPositions chooseFightSpot',
  retreat: 'actionPositions chooseFightSpot (retreat)',
  rules: 'actionBot (every brain)',
  prone: 'actionBot (the prone rule)',
  attention: 'actionBot (181: the view cone and the startle, with GameConfig.attention)',
  gun: 'actionBot (the gun brain)',
  skill: 'actionBot actionSkill (reaction, turn, aim, settle, strafe)',
  guns: 'actionBot (each gun\'s band, hold-fire, bursts)',
  kit: 'actionBot (the kit rules)',
  walk: 'actionBot (the walking row)',
  bike: 'actionBot (X31 bikes)',
  grenade: 'actionBot (the grenade rule)',
  rocket: 'actionBot (the rocket tube\'s draw rule, with the side\'s rocket stock)',
  gl40: 'actionBot (65.31 step 4: the 40 mm\'s draw rule)',
  armourAim: 'actionBot (185 step 5: the head by skill, armour in target choice)',
  strike: 'strikeRule crewOrCluster (strike drones, HE grenades)',
  look: 'actionBot (the look slots, 62.51)',
  bound: 'squadLead leadStep (the bound\'s cover posts) and actionBot (covering and area fire)',
  supply: 'supplyRead and actionBot (408: fire discipline when low, topping up before a push; finiteSupply)',
  underFire: 'actionBot (66.11: under fire, take cover — with coverMoves) and actionPositions chooseFireCover',
  gait: 'actionBot (63.8: when to move or sprint)',
  nav: 'actionBot (D3 navigation)',
};

const RULES: Record<string, RangeRule> = {
  'action.rocket.stockHuddle': { min: 1, max: 12, integer: true },
  'action.rocket.hullEdge': { min: 1, max: 2 },
  'action.rocket.shipHeightPeople': { min: 0, max: 60 },
  'action.rocket.shipLobHeightPeople': { min: 0, max: 20 },
  'action.rules.delay': { min: 0, max: 240, integer: true },
  'action.rules.positionPeriod': { min: 1, max: 240, integer: true },
  'action.rules.coverHealth': { min: 0, max: 100 },
  'action.rules.healBelow': { min: 0, max: 100 },
  'action.rules.reloadBelowShare': { min: 0, max: 1 },
  'action.skill.reactionJitterMin': { min: 0, max: 4 },
  'action.skill.reactionJitterSpread': { min: 0, max: 4 },
  'action.gun.searchTier.minSkill': { min: 0, max: 1 },
  '.coarseAzSteps': { min: 1, max: 48, integer: true },
  '.coarseElevSteps': { min: 1, max: 20, integer: true },
  '.coarsePowSteps': { min: 1, max: 16, integer: true },
  '.focusedTopN': { min: 1, max: 12, integer: true },
  '.focusedGridSize': { min: 1, max: 20, integer: true },
  '.refinePasses': { min: 0, max: 8, integer: true },
  '.randomSeeds': { min: 0, max: 16, integer: true },
  'action.nav.routeCache': { min: 1, max: 16, integer: true },
  'action.grenade.heReachShare': { min: 0, max: 1 },
  'action.grenade.heHiddenFreshSeconds': { min: 0, max: 120 },
  'action.grenade.smokeShare': { min: 0, max: 1 },
  'action.strike.minConfidence': { min: 0, max: 1 },
  'action.look.slots': { min: 1, max: 24, integer: true },
  'action.attention.bigThreat': { min: 1, max: 12, integer: true },
  'action.attention.focusDeg': { min: 0, max: 180 },
  'action.attention.peripheralDeg': { min: 0, max: 180 },
  'action.underFire.tries': { min: 1, max: 24, integer: true },
  'action.look.slotsAbove': { min: 1, max: 1000, integer: true },
  'action.look.unitsPerSlot': { min: 0.5, max: 100 },
  'action.supply.sideLow': { min: 0, max: 2 },
  'action.supply.unitLow': { min: 0, max: 1 },
  'action.supply.holdBeyondPeople': { min: 0, max: 400 },
  'action.supply.topUpShare': { min: 0, max: 1 },
};

export const ACTION_PARAM_DEFS: readonly BotParamDef[] = catalogueOf('action', ACTION_PARAMS, path => READERS[path.split('.')[1] as keyof ActionParams] ?? 'action bot', RULES);

/** The action params with the catalogue's `action.…` values laid on ACTION_PARAMS (ACTION_PARAMS itself when none). */
export function actionParamsFrom(values: BotParamSet | undefined): ActionParams {
  const entries = entriesUnder(values, 'action');
  let out = ACTION_PARAMS;
  for (const [parts, value] of entries) out = withPath(out, parts, value);
  return out;
}
